import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { apiClient } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Building2, Trash2, FolderLock, FileDown } from "lucide-react";
import DocumentsTab from "@/components/dataroom/DocumentsTab";
import ChecklistTab from "@/components/dataroom/ChecklistTab";
import QATab from "@/components/dataroom/QATab";
import ActivityTab from "@/components/dataroom/ActivityTab";
import AccessTab from "@/components/dataroom/AccessTab";
import { STAGES, STAGE_STYLE } from "@/lib/dataroom";

export default function DataRoom() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const dealId = params.get("id");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: deal, isLoading, isError, error } = useQuery({
    queryKey: ["deal", dealId],
    queryFn: async () => (await apiClient.get(`/deals/${dealId}`)).data,
    enabled: !!dealId,
    retry: false,
  });

  const stageMutation = useMutation({
    mutationFn: (stage) => apiClient.put(`/deals/${dealId}`, { stage }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["deal", dealId] }); qc.invalidateQueries({ queryKey: ["deals"] }); },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete(`/deals/${dealId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      toast.success("Datarom slettet");
      navigate(createPageUrl("Deals"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke slette"),
  });

  const handleReport = async () => {
    try {
      const res = await apiClient.get(`/deals/${dealId}/report`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Datarom-rapport - ${deal?.name || "deal"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Kunne ikke lage rapport");
    }
  };

  if (isLoading) {
    return <div className="min-h-screen bg-slate-50 p-8"><div className="max-w-6xl mx-auto"><div className="h-24 bg-white rounded-2xl animate-pulse" /></div></div>;
  }

  if (isError || !deal) {
    const status = error?.response?.status;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <FolderLock className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-xl font-semibold text-brand-ink">{status === 403 ? "Ingen tilgang" : "Datarom ikke funnet"}</h2>
          <p className="text-slate-500 mt-2">{status === 403 ? "Du har ikke tilgang til dette datarommet." : "Datarommet finnes ikke lenger."}</p>
          <Link to={createPageUrl("Deals")}><Button variant="outline" className="mt-4"><ArrowLeft className="w-4 h-4 mr-2" /> Til datarom</Button></Link>
        </div>
      </div>
    );
  }

  const canManage = deal.my_permission === "manage";
  const canAdminister = canManage && !deal.my_external;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to={createPageUrl("Deals")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-ink transition-colors mb-4">
          <ArrowLeft className="w-4 h-4" /> Alle datarom
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6 animate-fade-in">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-extrabold text-brand-ink tracking-tight">{deal.name}</h1>
              <Badge className={`border-0 ${STAGE_STYLE[deal.stage] || "bg-slate-100 text-slate-700"}`}>{STAGES[deal.stage] || deal.stage}</Badge>
            </div>
            {deal.related_customer_name && (
              <p className="mt-2 text-sm text-slate-500 flex items-center gap-1.5"><Building2 className="w-4 h-4" /> {deal.related_customer_name}</p>
            )}
            {deal.description && <p className="mt-1 text-sm text-slate-500 max-w-2xl">{deal.description}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" onClick={handleReport} data-testid="download-report-button">
              <FileDown className="w-4 h-4 mr-2" /> Rapport
            </Button>
            {canAdminister && (
              <>
                <Select value={deal.stage} onValueChange={(s) => stageMutation.mutate(s)}>
                  <SelectTrigger className="w-44 h-10" data-testid="deal-stage-edit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STAGES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setConfirmDelete(true)} data-testid="delete-deal-button">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        <Tabs defaultValue="documents" className="w-full">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="documents" data-testid="tab-documents">Dokumenter</TabsTrigger>
            <TabsTrigger value="checklist" data-testid="tab-checklist">Sjekkliste</TabsTrigger>
            <TabsTrigger value="qa" data-testid="tab-qa">Q&A</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Aktivitet</TabsTrigger>
            <TabsTrigger value="access" data-testid="tab-access">Tilgang</TabsTrigger>
          </TabsList>
          <TabsContent value="documents"><DocumentsTab deal={deal} canManage={canManage} /></TabsContent>
          <TabsContent value="checklist"><ChecklistTab deal={deal} canManage={canManage} /></TabsContent>
          <TabsContent value="qa"><QATab deal={deal} canManage={canManage} /></TabsContent>
          <TabsContent value="activity"><ActivityTab deal={deal} /></TabsContent>
          <TabsContent value="access"><AccessTab deal={deal} canManage={canManage} /></TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette datarommet?</AlertDialogTitle>
            <AlertDialogDescription>
              «{deal.name}» og alt innhold – dokumenter, sjekkliste, Q&A og aktivitetslogg – slettes permanent. Handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteMutation.mutate()}>Slett datarom</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
